import { DeleteIcon, DownloadIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  VStack,
} from '@chakra-ui/react';
import { getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AccessLinkTypes, ListAccessLinksQuery } from '@/@generated/codegen/API';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import { useConfirmModal } from '@/ui/ConfirmModal/ConfirmModal';
import { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { getAccessLinkUrl } from '@/utils/helpers/getAccessLinkUrl';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import { AccessLinkListRowType, getColumns } from './columns';
import { RefreshIcon } from '../icons/icons';

// --- CSV helper ---
const exportToCsv = (rows: AccessLinkListRowType[]) => {
  if (!rows.length) return;
  const header = ['Is Enabled', 'Url', 'Expiry date'];

  const csvRows = rows.map((row) => {
    const url = getAccessLinkUrl(row.accessCode);
    return [row.isEnabled ? 'Yes' : 'No', url, new Date(row.validUntil)];
  });

  const csvContent = [header, ...csvRows]
    .map((r) => r.map((x) => `"${x.toLocaleString()}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'access_links.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

type Props = {
  type: AccessLinkTypes;
  data: ListAccessLinksQuery;
  isLoading: boolean;
  pagination: ReturnType<typeof usePaginationState>;
  onBatchEnable: (objs: { id: string; isEnabled: boolean }[]) => void;
  onBatchDelete: (ids: string[]) => Promise<void>;
  onClose: () => void;
  onCreateClick: () => void;
  onRefreshClick: () => void;
  tablePrefix: string;
};

const filterExtraInfos: FilterExtraInfo<AccessLinkListRowType> = [];

export const AccessLinkPanelView = ({
  type,
  data,
  isLoading,
  pagination,
  onBatchEnable,
  onBatchDelete,
  onClose,
  onCreateClick,
  onRefreshClick,
  tablePrefix,
}: Props) => {
  const { t } = useTranslation();
  const totalCount = data.listAccessLinks.totalCount;
  const tableData = useMemo(() => {
    return data.listAccessLinks.edges.map((edge) => edge.node);
  }, [data]);

  const { sorting, onSortingChange } = useTableQueryParamsUtils(filterExtraInfos, tablePrefix);

  const columns = getColumns({
    type,
    onEnableClick: (id, isEnabled) => {
      onBatchEnable([{ id, isEnabled }]);
    },
  });

  const table = useReactTable({
    data: tableData,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      sorting,
    },

    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listAccessLinks.pageInfo),
    onSortingChange: onSortingChange,
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columns: columns,
  });

  const selectedRows = table.getSelectedRowModel().flatRows.map((r) => r.original);
  const allEnabled = selectedRows.every((r) => r.isEnabled);

  const confirmModalUtils = useConfirmModal();

  return (
    <Modal isOpen={true} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Access links</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {data && (
            <>
              <Flex gap="20px" justifyContent="center" alignItems="center" mb="4">
                <VStack w="full">
                  <HStack w="full">
                    <Button variant="primary" onClick={onCreateClick}>
                      Create new link(s)
                    </Button>

                    {selectedRows.length > 0 && (
                      <Menu>
                        <MenuButton as={Button} variant="outline">
                          Manage {selectedRows.length} selected
                        </MenuButton>
                        <MenuList>
                          <MenuItem
                            icon={<RefreshIcon size={16} />}
                            onClick={() => {
                              onBatchEnable(
                                selectedRows
                                  .filter((r) => r.isEnabled === allEnabled) // only include ones that need change
                                  .map((r) => ({ id: r.id, isEnabled: !allEnabled }))
                              );
                            }}
                          >
                            {allEnabled ? 'Disable all' : 'Enable all'}
                          </MenuItem>
                          <MenuItem
                            icon={<DownloadIcon boxSize={4} />}
                            onClick={() => exportToCsv(selectedRows)}
                          >
                            Export to CSV
                          </MenuItem>
                          <MenuDivider />

                          <MenuItem
                            icon={<DeleteIcon boxSize={4} />}
                            onClick={() => {
                              confirmModalUtils.confirm({
                                title: (
                                  <div>
                                    {t('accessLinkPanel.confirmDeleteTitle')}
                                    <br />
                                    {t('common.irreversableAction')}
                                  </div>
                                ),
                                onConfirm: () => {
                                  onBatchDelete(selectedRows.map((r) => r.id))
                                    .then(() => {
                                      onRefreshClick();
                                    })
                                    .catch((err) => {
                                      captureSentry({
                                        title: 'Error deleting access links',
                                        error: err,
                                      });
                                    });
                                },
                              });
                            }}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    )}

                    <Spacer />

                    <IconButton
                      aria-label="refresh-access-link-list"
                      icon={<RefreshIcon size={20} />}
                      color="gray"
                      variant="ghost"
                      onClick={onRefreshClick}
                      isLoading={isLoading}
                    />

                    <Pagination
                      table={table}
                      pageInfo={data.listAccessLinks.pageInfo}
                      totalRows={totalCount}
                      tablePrefix={tablePrefix}
                    />
                  </HStack>
                </VStack>
              </Flex>

              <TanstackTableView table={table} withRowSelection />
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
